import type { VerticalAlignmentMiddleByDefault } from '../../../amplienceTypes/partials/exported/layout-schema';
import type { ContentBlock as BannerContentBlock } from '../../../amplienceTypes/schemas/exported/banner-schema';
import type { ContentBlock as LocalizedBannerContentBlock } from '../../../amplienceTypes/schemas/exported/localized-banner-schema';
import type { ContentBlock as LogoBannerContentBlock } from '../../../amplienceTypes/schemas/exported/logo-banner-schema';
import type { SectionBanner } from '../../../amplienceTypes/schemas/exported/section-banner-schema';
import type { SectionHotspot } from '../../../amplienceTypes/schemas/exported/section-hotspot-schema';
import type { Title as SectionImage } from '../../../amplienceTypes/schemas/exported/section-image-schema';
import type { Title as SectionImageText } from '../../../amplienceTypes/schemas/exported/section-image-text-schema';
import type { SectionLocalizedBanner } from '../../../amplienceTypes/schemas/exported/section-localized-banner-schema';
import type { SectionText } from '../../../amplienceTypes/schemas/exported/section-text-schema';
import type { ContentBlock as SimpleBannerContentBlock } from '../../../amplienceTypes/schemas/exported/simple-banner-schema';
import type { ContentBlock as SimpleVideoBannerContentBlock } from '../../../amplienceTypes/schemas/exported/simple-video-banner-schema';
import type { CSSProperties, ReactElement } from 'react';

import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

import Attributes from './Attributes';
import { BodyImageSource, ImageSource } from './ImageSources';
import { hasHotspots } from '../../../utilities/amplience/hotspots';
import { gsPartialStyling } from '../../../utilities/constants/homepageHandlebars';
import { Button } from '../templates/Button';
import { withProfileToggle } from '~/components/homepage/withProfileToggle';
import { AiWatermark } from '~/components/shared/aiWatermark/AiWatermark';
import { parseHtmlWithFesLinks } from '~/components/shared/core/link/parseHtmlWithFesLinks';
import { media } from '~/components/shared/core/styles/media';
import { contentBlockZIndexLadder } from '~/components/unmanaged/styles/BaseStyledElements';
import { getAmplienceMeta, getDataAttributeValue } from '~/utilities/amplience';
import type { ParallaxBreakpoint, ParallaxConfig } from '~/utilities/helpers/getParallaxConfig';
import {
  getParallaxConfig,
  PARALLAX_DEFAULT_TIMING,
  PARALLAX_MODIFIER,
} from '~/utilities/helpers/getParallaxConfig';
import { getResponsiveImageSizes } from '~/utilities/helpers/getResponsiveImageSizes';
import { useAiWatermarkParams } from '~/utilities/hooks/useAiWatermarkParams';
import { capitalizeFirst } from '~/utilities/parsers';
import { normalizeObjectPosition } from '~/utilities/parsers/objectPosition';

const CLICKABLE_MODIFIER = 'contentBlock--clickable';
const VIMEO_MODIFIER = 'contentBlock--vimeoBackground';

/*
 * One timeline per block, declared on the block and referenced by every layer that
 * pans, so they cannot drift apart: view() would measure each layer's own box, and the
 * Vimeo container is routinely taller than the block it covers.
 *
 * It also shrinks where an overflow: hidden can freeze the animation. A view timeline
 * binds to the nearest ancestor scroll container of its *subject*, and the subject is
 * now the block -- so only ancestors above the block can capture it, never the block
 * itself or anything inside it.
 *
 * No timeline-scope: both layers are descendants of the block, which is within a named
 * timeline's default scope.
 */
const PARALLAX_TIMELINE = '--parallax-block';

// Slides the object-fit: cover crop through the source rather than moving the image
// box, so nothing is magnified and there is no geometry to keep in sync with the frame.
// Endpoints come from getParallaxConfig; the defaults below pan the whole crop.
const parallaxPan = keyframes`
  from {
    object-position: var(--object-position-x, 50%) var(--parallax-from, 100%);
  }
  to {
    object-position: var(--object-position-x, 50%) var(--parallax-to, 0%);
  }
`;

// The Vimeo embed is an iframe, so it has no cover crop to slide: it is panned by
// moving the container instead. VimeoPlayer sizes that container to cover the block
// and publishes how far it can travel, since only it knows the video's dimensions.
const vimeoPan = keyframes`
  from {
    transform: translateY(calc(var(--vimeo-parallax-shift, 0%) * -1));
  }
  to {
    transform: translateY(var(--vimeo-parallax-shift, 0%));
  }
`;

/*
 * Shared by both layers so they cannot drift apart in configuration.
 *
 * animation-duration: auto spans the timeline instead of running for 0s, and both it
 * and the timeline must stay after the animation shorthand, which resets them.
 *
 * The range comes from data-parallax-range; see PARALLAX_RANGES for what each option
 * spans and why a block at the top of the page needs a different one. The fallback
 * keeps the class alone working when it is toggled by hand in devtools.
 */
const parallaxTimeline = css`
  animation-duration: auto;
  animation-timeline: ${PARALLAX_TIMELINE};
  animation-range: var(--parallax-range, ${PARALLAX_DEFAULT_TIMING.range});
`;

// Per breakpoint: either the settings, or nothing to animate at all. Each breakpoint
// has its own image and frame, so its crop -- and so its strength -- differs.
const parallaxAt = (breakpoint?: ParallaxBreakpoint) =>
  breakpoint
    ? css`
        --parallax-from: ${breakpoint.from}%;
        --parallax-to: ${breakpoint.to}%;
        --parallax-zoom: ${breakpoint.zoom}%;
        --parallax-range: ${breakpoint.range};
        --parallax-easing: ${breakpoint.easing};
      `
    : css`
        .contentBlock-picture-image {
          animation-name: none;
        }
      `;

const sameStep = (a?: ParallaxBreakpoint, b?: ParallaxBreakpoint) =>
  a?.from === b?.from &&
  a?.to === b?.to &&
  a?.zoom === b?.zoom &&
  a?.range === b?.range &&
  a?.easing === b?.easing;

const isUniform = ({ mobile, tablet, desktop }: ParallaxConfig) =>
  sameStep(mobile, tablet) && sameStep(tablet, desktop);

const S = {
  ContentBlock: styled(Attributes)<{ $parallax?: ParallaxConfig }>`
    /* Also applied via BaseStyled.Banner, but that selector is :where() - zero specificity, and
       only the page-level wrappers use it. Interpolated here too so every block gets the ladder
       and its stacking context, whichever path rendered it. */
    ${contentBlockZIndexLadder}

    &.${CLICKABLE_MODIFIER} {
      .contentBlock-body-link:nth-child(n + 3) {
        display: none;
      }
    }
    &.${VIMEO_MODIFIER} {
      overflow: hidden;
    }

    .contentBlock-picture {
      position: relative;
      display: block;
    }

    /* --object-position comes in as an inline style; unset it falls back to centered. Mirrors
       the base contentBlockPictureImage rule for blocks rendered outside BaseStyled.Banner,
       and :where() keeps it weak enough for CMS-authored overrides to still win. */
    :where(.contentBlock-picture-image) {
      object-position: var(--object-position, 50% 50%);
    }

    &.${PARALLAX_MODIFIER} {
      @supports (animation-timeline: view()) {
        @media (prefers-reduced-motion: no-preference) {
          /* The block is the subject every layer measures against. The inset lives here
             rather than on each animation, so the sticky header is accounted for once.

             Nothing inside the block needs its overflow neutralised any more: a view
             timeline binds to the nearest scroll container *above* its subject, so the
             block's own overflow: hidden, and the picture's, no longer capture it.
             Ancestors still do -- that is what ParallaxSafeOverflow handles. Measured
             both ways in Chromium before removing the two overrides that were here. */
          view-timeline-name: ${PARALLAX_TIMELINE};
          view-timeline-inset: var(--top-nav-height) 0%;

          /* The endpoints belong in here with the things that read them. A declaration
             that escapes these guards is still readable off the computed style, and
             VimeoPlayer does exactly that for the zoom -- so left outside, it sized the
             video container to a zoom whose pan could never run, leaving the video
             cropped tighter than the still it fades in over. */
          ${({ theme, $parallax }) =>
            $parallax?.enabled &&
            (isUniform($parallax)
              ? parallaxAt($parallax.mobile)
              : css`
                  /* Boundaries match the <source> elements' 0 / 768 / 1024 exactly. */
                  @media ${media(theme).lessThan('sm')} {
                    ${parallaxAt($parallax.mobile)}
                  }

                  @media ${media(theme).between('sm', 'lg')} {
                    ${parallaxAt($parallax.tablet)}
                  }

                  @media ${media(theme).greaterThan('lg')} {
                    ${parallaxAt($parallax.desktop)}
                  }
                `)}

          .contentBlock-vimeoContainer {
            animation: ${vimeoPan} var(--parallax-easing, ${PARALLAX_DEFAULT_TIMING.easing}) both;
            ${parallaxTimeline}
          }

          .contentBlock-picture-image {
            /* data-parallax-scale zooms in to manufacture vertical crop for a source
               that has none. It goes on the width because cover only leaves spare
               height when the box's aspect is wider than the source's -- a taller box
               spends the same zoom sideways and pans nothing. The offset re-centres
               it, and both percentages resolve against the frame's width, which is
               always definite. Default 100%/0 leaves the box as the layout made it. */
            min-width: var(--parallax-zoom, 100%);

            /* Half the excess, pulled back, so the wider box stays centred. Derived
               from the zoom rather than passed alongside it, and sound because margin
               and min-width percentages both resolve against the frame's width. */
            margin-inline: calc((100% - var(--parallax-zoom, 100%)) / 2);

            /* Only the crop moves, never the box. The pan owns the vertical, so an
               authored data-object-position keeps its X and loses its Y -- the Y is
               what the pan sweeps through. */

            /* Easing comes from the range: each one puts the speed where it can be
               seen for the block's position on the page. */
            animation: ${parallaxPan} var(--parallax-easing, ${PARALLAX_DEFAULT_TIMING.easing}) both;
            ${parallaxTimeline}
          }
        }
      }
    }
  `,
  Title: styled.h2``,
  SubTitle: styled.h3``,
  Body: styled.div``,
};

const VimeoPlayer = dynamic(async () => (await import('../VimeoPlayer')).VimeoPlayer, {
  ssr: false,
});

const Hotspots = dynamic(async () => (await import('./Hotspots')).Hotspots, {
  ssr: false,
});

const Countdown = dynamic(async () => (await import('./Countdown')).Countdown);

export type ContentBlockDataSchema = SimpleVideoBannerContentBlock &
  SimpleBannerContentBlock &
  BannerContentBlock &
  LocalizedBannerContentBlock &
  LogoBannerContentBlock &
  SectionHotspot &
  SectionLocalizedBanner &
  SectionText &
  SectionImageText &
  SectionBanner &
  SectionImage & {
    columnCount?: number;
    viewportSizes?: string;
  };

const ContentBlockComponent = ({
  attributes,
  styling,
  layout,
  responsiveImage,
  vimeo,
  contentBody,
  columnCount,
  seo,
  viewportSizes,
  _meta,
}: ContentBlockDataSchema): ReactElement => {
  const { metaAttributes } = getAmplienceMeta(_meta);
  const { imageSizes } = getResponsiveImageSizes({ columnCount, viewportSizes });
  const { params, imageAltText } = useAiWatermarkParams(responsiveImage);

  const renderHotspots = responsiveImage?.images && hasHotspots(responsiveImage?.images);
  const contentBlockEl = useRef<HTMLDivElement>(null);
  const pictureImageEl = useRef<HTMLImageElement>(null);
  const stylingClasses = styling?.map(s => gsPartialStyling[s]).join(' ');
  const countdownDate = getDataAttributeValue(attributes?.dataAttributes, 'data-countdown-date');
  // Normalized before it reaches the element as an inline style.
  const objectPosition = normalizeObjectPosition(
    getDataAttributeValue(attributes?.dataAttributes, 'data-object-position')
  );
  const vimeoAudio = getDataAttributeValue(attributes?.dataAttributes, 'data-vimeo-audio');
  const vimeoMuteLabel = getDataAttributeValue(attributes?.dataAttributes, 'data-vimeo-mute-label');
  const vimeoUnmuteLabel = getDataAttributeValue(
    attributes?.dataAttributes,
    'data-vimeo-unmute-label'
  );
  const parallaxAmount = getDataAttributeValue(attributes?.dataAttributes, 'data-parallax');
  const parallaxScale = getDataAttributeValue(attributes?.dataAttributes, 'data-parallax-scale');
  const parallaxRange = getDataAttributeValue(attributes?.dataAttributes, 'data-parallax-range');
  // Memoised for a stable identity: VimeoPlayer keys its player effect on this, and a
  // fresh object every render would re-run it.
  const parallax = useMemo(
    () => getParallaxConfig({ amount: parallaxAmount, scale: parallaxScale, range: parallaxRange }),
    [parallaxAmount, parallaxScale, parallaxRange]
  );
  // Hotspots place their markers from the image's computed objectPosition, which is
  // the very thing being animated, so those two still cannot coexist. A Vimeo
  // background can: it fades in over the image, and both layers pan together.
  const enableParallax = parallax.enabled && !renderHotspots;
  // The schema field where a content type has one, falling back to the attributes the
  // Collection Hero already reads. Content types without a vimeo field -- Section Image
  // Text, for one -- can only carry the ids this way, so the same authored block would
  // otherwise play in a hero and be silently inert anywhere a ContentBlock renders it.
  const vimeoIds = vimeo ?? {
    mobile: getDataAttributeValue(attributes?.dataAttributes, 'data-vimeo-xs'),
    tablet: getDataAttributeValue(attributes?.dataAttributes, 'data-vimeo-sm'),
    desktop: getDataAttributeValue(attributes?.dataAttributes, 'data-vimeo-md'),
  };
  const hasVimeo = Boolean(vimeoIds.mobile || vimeoIds.tablet || vimeoIds.desktop);
  const titleHtml =
    (contentBody?.title?.htmlEditor?.html as string) || (contentBody?.title as string);
  const subTitleHtml =
    (contentBody?.subTitle?.htmlEditor?.html as string) || (contentBody?.subTitle as string);
  const bodyHtml = (contentBody?.body?.htmlEditor?.html as string) || (contentBody?.body as string);
  const parsedBodyContent = useMemo(
    () => (bodyHtml ? parseHtmlWithFesLinks(bodyHtml) : undefined),
    [bodyHtml]
  );

  const getVerticalAlignmentClass = () => {
    const verticalAlignment = layout?.verticalAlignment as VerticalAlignmentMiddleByDefault;

    return `${
      verticalAlignment?.mobile
        ? ` contentBlock--xsBodyVerticalAlign${capitalizeFirst(verticalAlignment.mobile)}`
        : ''
    }${
      verticalAlignment?.tablet
        ? ` contentBlock--smBodyVerticalAlign${capitalizeFirst(verticalAlignment.tablet)}`
        : ''
    }${
      verticalAlignment?.desktop
        ? ` contentBlock--mdBodyVerticalAlign${capitalizeFirst(verticalAlignment.desktop)}`
        : ''
    }`;
  };

  useEffect(() => {
    if (contentBlockEl?.current?.classList.contains(CLICKABLE_MODIFIER)) {
      const contentBlockBodyLinkNodeList = contentBlockEl.current.querySelectorAll(
        '.js-contentBlock-body-link'
      );
      const contentBlockBodyLink: Element[] = [];
      let clickList: Element[] = [];
      const hitareaList: HTMLElement[] = [];

      contentBlockBodyLinkNodeList.forEach(elm => {
        contentBlockBodyLink.push(elm);
      });

      if (contentBlockBodyLink.length > 2) {
        clickList = contentBlockBodyLink.slice(2);
      } else {
        clickList = contentBlockBodyLink;
      }

      if (clickList.length > 0) {
        clickList.forEach(elm => {
          const hitarea = document.createElement('div');

          hitarea.style.cssText = `
            position: absolute;
            inset: 20px;
            cursor: pointer;
            z-index: var(--contentBlock-clickable-z-index, 10);
            background-color: rgba(255, 0, 0, 0);
          `;

          contentBlockEl.current?.appendChild(hitarea);
          hitareaList.push(hitarea);
          hitarea.addEventListener('click', event => {
            if (event.type === 'click') {
              (elm as HTMLElement).click();
            }
          });

          [hitarea, elm].forEach(element => {
            element.addEventListener('mouseenter', () => {
              contentBlockEl.current?.classList.add('is-hovered');
            });
            element.addEventListener('mouseleave', () => {
              contentBlockEl.current?.classList.add('is-hovered');
            });
          });
        });

        if (clickList.length === 2) {
          const rectData = contentBlockEl.current.getBoundingClientRect();
          let minWidth: number;

          clickList.forEach((elm, i) => {
            const elmRectData = elm.getBoundingClientRect();
            const htmlElm = elm as HTMLElement;

            if (i === 0) {
              const right =
                (elmRectData.left +
                  htmlElm.offsetWidth +
                  Number.parseInt(htmlElm.style.marginRight || '0', 10) -
                  rectData.left) /
                rectData.width;

              hitareaList[i].style.right = `${100 - right * 100}%`;
            } else {
              const localLeft =
                (elmRectData.left -
                  Number.parseInt(htmlElm.style.marginLeft || '0', 10) -
                  rectData.left) /
                rectData.width;

              hitareaList[i].style.left = `${localLeft * 100}%`;
            }

            const width = hitareaList[i].offsetWidth / rectData.width;

            minWidth = minWidth && minWidth < width ? minWidth : width;
          });

          clickList.forEach((elm, i) => {
            hitareaList[i].style.width = `${minWidth * 100}%`;

            if (i === 0) {
              hitareaList[i].style.left = 'auto';
            } else {
              hitareaList[i].style.right = 'auto';
            }
          });
        }
      }

      return () => {
        hitareaList?.forEach(elm => {
          elm.remove();
        });
      };
    }

    return () => {};
  }, []);

  return (
    <S.ContentBlock
      {...attributes}
      {...metaAttributes}
      additionalClassNames={`contentBlock js-contentBlock${
        layout?.textAlignment?.tablet
          ? ` contentBlock--smBodyTextAlign${capitalizeFirst(layout.textAlignment.tablet)}`
          : ''
      }${
        layout?.textAlignment?.desktop
          ? ` contentBlock--mdBodyTextAlign${capitalizeFirst(layout.textAlignment.desktop)}`
          : ''
      }${getVerticalAlignmentClass()}${hasVimeo ? ` ${VIMEO_MODIFIER}` : ''}${
        enableParallax ? ` ${PARALLAX_MODIFIER}` : ''
      } ${stylingClasses ?? ''}`}
      {...(vimeoIds.mobile ? { 'data-vimeo-xs': vimeoIds.mobile } : {})}
      {...(vimeoIds.tablet ? { 'data-vimeo-sm': vimeoIds.tablet } : {})}
      {...(vimeoIds.desktop ? { 'data-vimeo-md': vimeoIds.desktop } : {})}
      style={
        {
          '--object-position': objectPosition,
          // The pan replaces object-position wholesale -- a keyframe cannot address one
          // axis -- and an animation outranks even an inline style, so the rule that
          // reads --object-position is not enough to keep the authored focal point.
          // Publishing the horizontal half on its own lets the keyframes hold it while
          // they sweep the vertical.
          '--object-position-x': objectPosition?.split(' ')[0],
        } as CSSProperties
      }
      $parallax={enableParallax ? parallax : undefined}
      ref={contentBlockEl}
    >
      {(responsiveImage?.desktop ||
        responsiveImage?.tablet ||
        responsiveImage?.mobile ||
        responsiveImage?.images ||
        responsiveImage?.customSources) && (
        <picture
          className={`contentBlock-picture js-contentBlock-picture ${
            renderHotspots ? 'js-has-hotspots' : ''
          }`}
        >
          <ImageSource {...responsiveImage} imageSizes={imageSizes} />
          <img
            ref={pictureImageEl}
            decoding="async"
            className={`contentBlock-picture-image js-contentBlock-picture-image ${
              renderHotspots ? 'js-has-hotspots-image' : ''
            }`}
            alt={imageAltText ?? 'responsive-image'}
            loading={responsiveImage.lazyload === 'off' ? 'eager' : 'lazy'}
          />
          <AiWatermark {...params} className="ai-icon-container" />
        </picture>
      )}

      {hasVimeo && (
        <VimeoPlayer
          {...vimeoIds}
          contentBlockEl={contentBlockEl}
          objectPosition={objectPosition}
          parallax={enableParallax ? parallax : undefined}
          audio={vimeoAudio}
          muteLabel={vimeoMuteLabel}
          unmuteLabel={vimeoUnmuteLabel}
        />
      )}

      {contentBody && (
        <div
          className={`contentBlock-body js-contentBlock-body${
            layout?.tabletOffset ? ` contentBlock-body--smOffset${layout.tabletOffset}` : ''
          }${layout?.desktopOffset ? ` contentBlock-body--mdOffset${layout.desktopOffset}` : ''}`}
        >
          <div
            className={`contentBlock-bodyAlign${
              layout?.tabletWidth ? ` contentBlock-bodyAlign--smWidth${layout.tabletWidth}` : ''
            }${
              layout?.desktopWidth ? ` contentBlock-bodyAlign--mdWidth${layout.desktopWidth}` : ''
            }${
              layout?.textAlignment?.mobile
                ? ` contentBlock-bodyAlign--smTextAlign${capitalizeFirst(
                    layout?.textAlignment?.mobile
                  )}`
                : ''
            }`}
          >
            {layout?.verticalOffset && (
              <>
                {layout.verticalOffset.mobile && (
                  <div
                    className="is-mobile is-visible--xxsmall"
                    style={{ marginTop: layout.verticalOffset.mobile }}
                  />
                )}
                {layout.verticalOffset.tablet && (
                  <div
                    className="is-tablet is-visible--xsmall is-visible--small"
                    style={{ marginTop: layout.verticalOffset.tablet }}
                  />
                )}
                {layout.verticalOffset.desktop && (
                  <div
                    className="is-desktop is-hidden--xxsmall is-hidden--xsmall is-hidden--small"
                    style={{ marginTop: layout.verticalOffset.desktop }}
                  />
                )}
              </>
            )}
            {(contentBody?.responsiveBodyImage?.desktop ||
              contentBody?.responsiveBodyImage?.tablet ||
              contentBody?.responsiveBodyImage?.mobile ||
              contentBody?.responsiveBodyImage?.images) && (
              <picture className="contentBlock-body-picture js-contentBlock-body-picture">
                <BodyImageSource {...contentBody.responsiveBodyImage} />
                <img
                  decoding="async"
                  className="contentBlock-body-picture-image js-contentBlock-body-picture-image"
                  alt={contentBody.responsiveBodyImage.alt ?? 'responsive-body-image'}
                  loading="lazy"
                />
              </picture>
            )}

            {titleHtml && (
              <S.Title
                className="contentBlock-body-title js-contentBlock-body-title"
                suppressHydrationWarning
                dangerouslySetInnerHTML={{ __html: titleHtml }}
                as={seo?.title || 'h2'}
              />
            )}

            {subTitleHtml &&
              (countdownDate && contentBody.subTitle ? (
                <Countdown
                  date={countdownDate}
                  content={contentBody.subTitle}
                  seo={seo?.subTitle}
                />
              ) : (
                <S.SubTitle
                  className="contentBlock-body-subTitle js-contentBlock-body-subTitle"
                  suppressHydrationWarning
                  dangerouslySetInnerHTML={{ __html: subTitleHtml }}
                  as={seo?.subTitle || 'h3'}
                />
              ))}
            {parsedBodyContent && (
              <S.Body
                className="contentBlock-body-text js-contentBlock-body-text"
                suppressHydrationWarning
                as={seo?.body || 'div'}
              >
                {parsedBodyContent}
              </S.Body>
            )}
            {contentBody.buttons &&
              contentBody.buttons.length > 0 &&
              contentBody.buttons.filter(button => button.text || button.url).length > 0 && (
                <div className="contentBlock-body-links js-contentBlock-body-links">
                  {contentBody.buttons.map(
                    (button, i) => (button.text || button.url) && <Button key={i} {...button} />
                  )}
                </div>
              )}
          </div>
        </div>
      )}

      {responsiveImage?.images && renderHotspots && (
        <Hotspots images={responsiveImage.images} imageRef={pictureImageEl} />
      )}
    </S.ContentBlock>
  );
};

export const ContentBlock = withProfileToggle(
  ContentBlockComponent,
  props => props.attributes?.dataAttributes
);
